import React from 'react';
import { Badge } from '../../../components/ui/Badge';

const fmt = (n) => Number(n || 0).toLocaleString('ko-KR');
const hasNonZeroAmount = (item) => Number(item?.amount) !== 0;
const isDeductionRuleEnabled = (rules, key) => rules?.[key]?.enabled === true;
const workDaysWithPaidLeave = (record) => {
  const paid = Number(record?.paid_leave_days) || 0;
  const work = record?.work_days ?? 0;
  return paid > 0 ? `${work}일 (유급 ${paid}일)` : `${work}일`;
};

function moneyClass(item) {
  return item.item_type === 'deduction' || Number(item.amount) < 0
    ? 'text-red-600 dark:text-red-400'
    : 'text-[var(--text)]';
}

function ItemRow({ item }) {
  return (
    <div className="flex items-center justify-between py-1.5 px-1 border-b border-[var(--border)]/50 last:border-0">
      <span className="text-[11px] text-[var(--text-sub)]">{item.item_name}</span>
      <span className={`text-xs font-semibold tabular-nums ${moneyClass(item)}`}>
        {fmt(item.amount)}원
      </span>
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className="flex flex-col items-center px-1 py-1 rounded-lg bg-[var(--bg-subtle)]">
      <span className="text-[9px] font-medium text-[var(--text-muted)] mb-0.5">{label}</span>
      <span className={`text-xs font-bold ${warn ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--text-sub)]'}`}>
        {value}
      </span>
    </div>
  );
}

export default function PayslipDetail({ detail }) {
  if (!detail?.record) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-8 text-center text-sm text-[var(--text-muted)]">
        급여명세서를 선택해 주세요.
      </div>
    );
  }

  const { period, record } = detail;
  const items = (record.items || []).filter(hasNonZeroAmount);
  const earnings = items.filter((i) => i.item_type === 'earning');
  const deductions = items.filter((i) => i.item_type === 'deduction');
  const rules = record.deduction_rules || {};
  const hasAttendance = Boolean(record.attendance_time_type);
  const workMins = Number(record.total_work_minutes) || 0;
  const workH = Math.floor(workMins / 60);
  const workM = workMins % 60;
  const hourly = record.monthly_hours ? Math.floor(Number(record.ordinary_wage || 0) / Number(record.monthly_hours)) : 0;

  const statItems = [
    { label: '소정근로일', value: hasAttendance ? `${record.biz_days || 0}일` : '해당없음' },
    { label: '출근일(유급휴가)', value: hasAttendance ? workDaysWithPaidLeave(record) : '해당없음' },
    { label: '결근일', value: hasAttendance ? `${record.absent_days}일` : '-', warn: hasAttendance && Number(record.absent_days) > 0, enabled: isDeductionRuleEnabled(rules, 'absent') },
    { label: '총 근로시간', value: hasAttendance ? `${workH}h ${workM}m` : '-' },
    { label: '지각', value: `${record.late_count}회${Number(record.late_minutes) > 0 ? ` (${record.late_minutes}분)` : ''}`, warn: Number(record.late_count) > 0, enabled: isDeductionRuleEnabled(rules, 'late') },
    { label: '조퇴', value: `${record.early_leave_count}회${Number(record.early_leave_minutes) > 0 ? ` (${record.early_leave_minutes}분)` : ''}`, warn: Number(record.early_leave_count) > 0, enabled: isDeductionRuleEnabled(rules, 'early_leave') },
    { label: '무급휴가', value: `${record.unpaid_leave_days}일`, warn: Number(record.unpaid_leave_days) > 0, enabled: isDeductionRuleEnabled(rules, 'unpaid_leave') },
    { label: '주휴공제', value: Number(record.weekly_holiday_weeks) > 0 ? `${record.weekly_holiday_weeks}주` : '-' },
  ].filter((item) => item.enabled !== false);

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-4 sm:p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-base font-extrabold text-[var(--text)]">{period.year}년 {period.month}월 급여명세서</h2>
          <div className="text-xs text-[var(--text-muted)] mt-1">
            {period.company_name || period.company_code} · {period.period_start} ~ {period.period_end} · 지급일 {period.pay_date || '-'}
          </div>
        </div>
        <Badge variant="green" className="text-[10px]">확정</Badge>
      </div>

      {Number(record.ordinary_wage) > 0 && (
        <div className="flex items-center justify-between px-3 py-1.5 mb-3 rounded-lg bg-accent-500/5 dark:bg-accent-500/10 border border-accent-200/30 dark:border-accent-500/20">
          <span className="text-[11px] font-semibold text-accent-600 dark:text-accent-400">통상임금</span>
          <div className="text-right">
            <span className="text-xs font-bold text-[var(--text)]">{fmt(record.ordinary_wage)}원</span>
            {record.monthly_hours && <span className="text-[10px] text-[var(--text-muted)] ml-1.5">(시급 {fmt(hourly)}원)</span>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-3">
        {statItems.map((item) => <Stat key={item.label} {...item} />)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">지급 항목</span>
          </div>
          {earnings.length ? earnings.map((item) => <ItemRow key={item.id} item={item} />) : (
            <div className="text-xs text-[var(--text-muted)] py-3">지급 항목이 없습니다.</div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
            <span className="text-xs font-bold text-red-600 dark:text-red-400">공제 항목</span>
          </div>
          {deductions.length ? deductions.map((item) => <ItemRow key={item.id} item={item} />) : (
            <div className="text-xs text-[var(--text-muted)] py-3">공제 항목이 없습니다.</div>
          )}
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-[var(--border)]">
        <div className="grid grid-cols-2 divide-x divide-[var(--border)]">
          <div className="p-3 text-center bg-[var(--bg-subtle)]">
            <div className="text-[9px] font-medium text-[var(--text-muted)] mb-0.5">지급 합계</div>
            <div className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 tabular-nums">{fmt(record.total_earning)}</div>
          </div>
          <div className="p-3 text-center bg-[var(--bg-subtle)]">
            <div className="text-[9px] font-medium text-[var(--text-muted)] mb-0.5">공제 합계</div>
            <div className="text-sm font-extrabold text-red-600 dark:text-red-400 tabular-nums">{fmt(record.total_deduction)}</div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-2 bg-pine-900 dark:bg-pine-800">
          <span className="text-xs font-semibold text-white/70">실수령액</span>
          <span className="text-lg font-black text-white tabular-nums">{fmt(record.net_pay)}원</span>
        </div>
      </div>

      {record.is_adjusted && (
        <div className="mt-3">
          <Badge variant="yellow" className="text-[10px]">수동 조정됨</Badge>
        </div>
      )}
    </div>
  );
}
